/**
 * Minimal inline-SVG sparkline.
 *
 * Used in the admin Users + Accounts tables to show 30-day ticket
 * creation activity per row at a glance. Renders as bars (not a
 * line) so a single-day spike is visible even when surrounding
 * days are zero — line charts smooth those out and lose the signal.
 *
 * Width is fixed (~78px) so the column doesn't shift around per
 * row. Height adapts to a value-relative scale; bars never shrink
 * below 1px so a streak of zeros still renders a baseline.
 */

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Hover tooltip prefix, e.g. "tickets created". */
  label?: string;
}

export function Sparkline({ values, width = 78, height = 20, label = 'created' }: SparklineProps) {
  if (values.length === 0) return null;
  const max = Math.max(1, ...values);
  const total = values.reduce((acc, v) => acc + v, 0);
  const barCount = values.length;
  const gap = 1;
  const totalGap = gap * (barCount - 1);
  const barWidth = Math.max(1, (width - totalGap) / barCount);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`${total} ${label} in the last ${barCount} days`}
    >
      <title>{`${total} ${label} · last ${barCount} days · peak ${max}`}</title>
      {values.map((v, i) => {
        const h = v === 0 ? 1 : Math.max(1, Math.round((v / max) * height));
        const x = i * (barWidth + gap);
        const y = height - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            fill={v === 0 ? 'var(--border)' : 'var(--accent, #f97316)'}
            opacity={v === 0 ? 0.5 : 1}
          />
        );
      })}
    </svg>
  );
}
